import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OnOurWayLogService, OnOurWayLogRow, OnOurWayAvailabilityRow } from './onourwaylog.service';

interface RaterOption { employee_id: number; employee_name: string; region: string; presses: number; }
interface RaterSummary {
  employee_id: number; employee_name: string; region: string;
  available: number; pressedJobs: number; rate: number;
  presses: number; withClockin: number; avgMinutes: number;
}
interface DaySummary { date: string; available: number; pressedJobs: number; rate: number; hidden: number; }

@Component({
  selector: 'app-onourwaylog',
  templateUrl: './onourwaylog.component.html',
  styleUrls: ['./onourwaylog.component.css']
})
export class OnOurWayLogComponent implements OnInit {

  // Defaults to today only; auditors widen the range as needed.
  from = OnOurWayLogComponent.ymd(new Date());
  to = OnOurWayLogComponent.ymd(new Date());
  regions: string[] = [];
  raters: RaterOption[] = [];
  selectedRegions: string[] = [];
  selectedRaters: number[] = [];

  rows: OnOurWayLogRow[] = [];
  jobs: OnOurWayAvailabilityRow[] = [];
  loading = false;
  error = '';
  updatedAt: Date = null;

  stats = { presses: 0, withClockin: 0, avgMinutes: null as number, available: 0, pressedJobs: 0, rate: null as number, hidden: 0 };
  perRater: RaterSummary[] = [];
  perDay: DaySummary[] = [];

  constructor(private service: OnOurWayLogService) {}

  static daysAgo(n: number): Date { const d = new Date(); d.setDate(d.getDate() - n); return d; }
  static ymd(d: Date): string {
    const p = (n: number) => (n < 10 ? '0' : '') + n;
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
  }

  ngOnInit(): void {
    this.loadFilters();
    this.load();
  }

  loadFilters(): void {
    this.service.getFilters().subscribe((res: any) => {
      if (res && !res.Error) {
        this.regions = res.Regions || [];
        this.raters = res.Raters || [];
      }
    }, () => { /* dropdowns stay empty; the tables still load */ });
  }

  // Raters shown in the rater dropdown: narrowed to the selected regions when any are picked.
  get raterOptions(): RaterOption[] {
    if (!this.selectedRegions.length) { return this.raters; }
    return this.raters.filter(r => this.selectedRegions.indexOf(r.region) !== -1);
  }

  onRegionsChange(): void {
    const visible = new Set(this.raterOptions.map(r => r.employee_id));
    this.selectedRaters = this.selectedRaters.filter(id => visible.has(id));
    this.load();
  }

  // Presses (by press date) and eligible jobs (by schedule date) load together; either failing reports an error.
  load(): void {
    if (!this.from || !this.to) { return; }
    if (this.from > this.to) { this.error = 'From date is after To date.'; return; }
    this.loading = true;
    this.error = '';
    const emps = this.selectedRaters.map(Number);
    const asError = (what: string) => catchError(err => of({ Error: true, Message: 'Could not load ' + what + ': ' + (err && err.message ? err.message : err) }));
    forkJoin([
      this.service.getLog(this.from, this.to, this.selectedRegions, emps).pipe(asError('presses')),
      this.service.getAvailability(this.from, this.to, this.selectedRegions, emps).pipe(asError('eligible jobs'))
    ]).subscribe(([log, avail]: any[]) => {
      this.loading = false;
      this.rows = log.Error ? [] : (log.Rows || []);
      this.jobs = avail.Error ? [] : (avail.Rows || []);
      this.error = [log, avail].filter(r => r.Error).map(r => r.Message).join(' ');
      this.computeStats();
      this.updatedAt = new Date();
    });
  }

  clearFilters(): void {
    this.selectedRegions = [];
    this.selectedRaters = [];
    this.load();
  }

  computeStats(): void {
    const withClockin = this.rows.filter(r => r.diff_minutes !== null && r.diff_minutes !== undefined);
    const available = this.jobs.filter(j => j.available);
    const pressedJobs = available.filter(j => j.pressed);
    this.stats = {
      presses: this.rows.length,
      withClockin: withClockin.length,
      avgMinutes: withClockin.length ? withClockin.reduce((s, r) => s + r.diff_minutes, 0) / withClockin.length : null,
      available: available.length,
      pressedJobs: pressedJobs.length,
      rate: available.length ? pressedJobs.length / available.length : null,
      hidden: this.jobs.length - available.length
    };

    // Per rater: available/pressed from the eligible jobs (assigned technician), press timing from the log.
    const byRater = new Map<number, RaterSummary & { sum: number }>();
    const rater = (id: number, name: string, region: string) => {
      let s = byRater.get(id);
      if (!s) {
        s = { employee_id: id, employee_name: name, region: region, available: 0, pressedJobs: 0, rate: null,
              presses: 0, withClockin: 0, avgMinutes: null, sum: 0 };
        byRater.set(id, s);
      }
      if (!s.employee_name && name) { s.employee_name = name; }
      if (!s.region && region) { s.region = region; }
      return s;
    };
    this.jobs.forEach(j => {
      if (!j.available) { return; }
      const s = rater(j.employee_id, j.employee_name, j.region);
      s.available++;
      if (j.pressed) { s.pressedJobs++; }
    });
    this.rows.forEach(r => {
      const s = rater(r.employee_id, r.employee_name, r.region);
      s.presses++;
      if (r.diff_minutes !== null && r.diff_minutes !== undefined) { s.withClockin++; s.sum += r.diff_minutes; }
    });
    this.perRater = Array.from(byRater.values()).map(s => {
      s.avgMinutes = s.withClockin ? s.sum / s.withClockin : null;
      s.rate = s.available ? s.pressedJobs / s.available : null;
      delete s.sum;
      return s;
    }).sort((a, b) => b.available - a.available || b.presses - a.presses ||
      String(a.employee_name || '').localeCompare(String(b.employee_name || '')));

    // Per schedule day, newest first.
    const byDay = new Map<string, DaySummary>();
    this.jobs.forEach(j => {
      let d = byDay.get(j.schedule_date);
      if (!d) { d = { date: j.schedule_date, available: 0, pressedJobs: 0, rate: null, hidden: 0 }; byDay.set(j.schedule_date, d); }
      if (!j.available) { d.hidden++; return; }
      d.available++;
      if (j.pressed) { d.pressedJobs++; }
    });
    this.perDay = Array.from(byDay.values()).map(d => {
      d.rate = d.available ? d.pressedJobs / d.available : null;
      return d;
    }).sort((a, b) => b.date.localeCompare(a.date));
  }

  fmtRate(r: number): string {
    return r === null || r === undefined ? '—' : Math.round(r * 100) + '%';
  }

  rateClass(r: number): string {
    if (r === null || r === undefined) { return 'none'; }
    if (r >= 0.8) { return 'ok'; }
    if (r >= 0.5) { return 'warn'; }
    return 'late';
  }

  // Times render in the rater's own timezone (recorded at press time), labelled with its abbreviation.
  fmtTime(iso: string, tz: string): string {
    if (!iso) { return ''; }
    const d = new Date(iso);
    const opts: any = { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
    try {
      return d.toLocaleString('en-US', Object.assign({ timeZone: tz || 'America/New_York' }, opts));
    } catch (e) {
      return d.toLocaleString('en-US', opts);
    }
  }

  fmtMinutes(m: number): string {
    if (m === null || m === undefined || isNaN(m)) { return ''; }
    const abs = Math.abs(m);
    if (abs < 60) { return Math.round(m) + ' min'; }
    const h = Math.floor(abs / 60);
    const mm = Math.round(abs % 60);
    return (m < 0 ? '-' : '') + h + 'h ' + mm + 'm';
  }

  diffClass(m: number): string {
    if (m === null || m === undefined) { return 'none'; }
    if (m > 180) { return 'late'; }
    if (m > 90) { return 'warn'; }
    return 'ok';
  }

  private downloadCsv(lines: string[], name: string): void {
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name + '_' + this.from + '_to_' + this.to + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private static csvEsc(v: any): string {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }

  // Export the filtered press rows as CSV (what's on screen, not a server query).
  exportCsv(): void {
    const cols = ['Pressed', 'Timezone', 'Rater', 'Region', 'Contact', 'Phone', 'Street', 'Subdivision', 'Lot', 'Job Type',
      'Schedule Date', 'Job ID', 'First Clock-in', 'Diff (min)'];
    const lines = [cols.join(',')].concat(this.rows.map(r => [
      this.fmtTime(r.pressed_at, r.pressed_tz), r.pressed_tz, r.employee_name, r.region, r.contact_name, r.contact_phone,
      r.street_address, r.subdivision_name, r.lot, r.job_type, r.schedule_date, r.job_id,
      this.fmtTime(r.first_clockin, r.pressed_tz), r.diff_minutes === null ? '' : r.diff_minutes
    ].map(OnOurWayLogComponent.csvEsc).join(',')));
    this.downloadCsv(lines, 'on-our-way-log');
  }

  // Export the eligible jobs (one row per job, with pressed / hidden reason).
  exportJobsCsv(): void {
    const cols = ['Schedule Date', 'Rater', 'Region', 'Subdivision', 'Lot', 'Street', 'Job Type', 'Contact', 'Phone',
      'Button Shown', 'Hidden Reason', 'Pressed', 'Presses', 'First Press', 'Job ID'];
    const lines = [cols.join(',')].concat(this.jobs.map(j => [
      j.schedule_date, j.employee_name, j.region, j.subdivision_name, j.lot, j.street_address, j.job_type, j.contact_name, j.contact_phone,
      j.available ? 'yes' : 'no', j.hidden_reason || '', j.pressed ? 'yes' : 'no', j.presses,
      j.first_press_at ? this.fmtTime(j.first_press_at, null) : '', j.job_id
    ].map(OnOurWayLogComponent.csvEsc).join(',')));
    this.downloadCsv(lines, 'on-our-way-jobs');
  }

  trackJob(i: number, j: OnOurWayAvailabilityRow): number { return j.job_id; }
  trackRow(i: number, r: OnOurWayLogRow): number { return r.id; }
  trackDay(i: number, d: DaySummary): string { return d.date; }
}
